import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { cloneDeep } from 'lodash';
import { Toggle } from 'components/Toggle';
import {
  getPreciseDecimal,
  getPriceValue,
  getStoreCurrencyFormatter,
} from 'utils';
import { FixedModal } from 'components/FixedModal';
import { PercentageModal } from 'components/PercentageModal';
import { useStore } from 'context/store-context';
import { GET_PROTECTION_SETTINGS } from 'gql/queries';
import {
  UPDATE_PROTECTION_SETTINGS,
  UPDATE_STORE,
  UPDATE_STORE_CUSTOMER_AUTO_OPT_IN_TAGS,
} from 'gql/mutations';
import { RuleItem } from 'types/plans';
import { AllFixedRulesModal } from 'components/AllFixedRulesModal';
import { withNotification } from 'components/Notification';
import { NotificationTypes, apiErrorHandler } from 'utils';
import OfferingSettings from './OfferingSettings';
import useGodUser from 'hooks/useGodUser';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import {
  CashIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  PlusIcon,
  RefreshIcon,
  ShieldCheckIcon,
  TagIcon,
  XIcon,
} from '@heroicons/react/outline';

type TagInputCardProps = {
  title: string;
  description: string;
  placeholder: string;
  items: string[];
  onChangeItems: (items: string[]) => void | Promise<void>;
  disabled?: boolean;
};

type ExclusionField =
  | 'excludedProductSkus'
  | 'excludedProductTags'
  | 'excludedCustomerTags';

const TagInputCard = ({
  title,
  description,
  placeholder,
  items,
  onChangeItems,
  disabled,
}: TagInputCardProps) => {
  const [inputValue, setInputValue] = useState('');
  const [duplicateMessage, setDuplicateMessage] = useState('');
  const safeItems = Array.isArray(items)
    ? items.filter((item): item is string => typeof item === 'string')
    : [];
  const normalizedInputValue = inputValue.trim().toLocaleLowerCase();
  const itemAlreadyExists =
    normalizedInputValue !== '' &&
    safeItems.some(
      (item) => item.trim().toLocaleLowerCase() === normalizedInputValue,
    );

  const addItem = () => {
    const nextItem = inputValue.trim();
    if (!nextItem || disabled) return;

    if (itemAlreadyExists) {
      setDuplicateMessage(`${nextItem} already exists.`);
      return;
    }

    onChangeItems([...safeItems, nextItem]);
    setInputValue('');
    setDuplicateMessage('');
  };

  const removeItem = (indexToRemove: number) => {
    if (disabled) return;

    onChangeItems(safeItems.filter((_, index) => index !== indexToRemove));
    setDuplicateMessage('');
  };

  return (
    <div className="rounded-lg border border-slate-100 bg-white p-4">
      <div className="grid gap-4 md:grid-cols-2 md:items-start">
        <div className="space-y-1">
          <h5 className="text-base font-semibold text-slate-900">{title}</h5>
          <p className="text-sm leading-6 text-slate-500">{description}</p>
        </div>
        <div className="flex gap-2">
          <input
            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-blue-600 focus:ring-1 focus:ring-blue-600 disabled:bg-slate-100 disabled:text-slate-400"
            placeholder={placeholder}
            value={inputValue}
            disabled={disabled}
            onChange={(event) => {
              setInputValue(event.target.value);
              setDuplicateMessage('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addItem();
              }
            }}
          />
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            disabled={disabled || inputValue.trim() === '' || itemAlreadyExists}
            onClick={addItem}
          >
            <PlusIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Add</span>
          </button>
        </div>
      </div>
      {(duplicateMessage || itemAlreadyExists) && (
        <p className="mt-3 text-sm font-medium text-amber-700">
          {duplicateMessage || `${inputValue.trim()} already exists.`}
        </p>
      )}
      {safeItems.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {safeItems.map((item, index) => (
            <span
              key={`${item}-${index}`}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-slate-800"
            >
              {item}
              <button
                type="button"
                className="text-slate-500 transition hover:text-red-600 disabled:cursor-not-allowed disabled:text-slate-300"
                aria-label={`Remove ${item}`}
                disabled={disabled}
                onClick={() => removeItem(index)}
              >
                <XIcon className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div className="mt-4 flex items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm italic text-slate-500">
          No exclusions added
        </div>
      )}
    </div>
  );
};

export const Protection = withNotification(({ showNotification }: any) => {
  const isGodUser = useGodUser();
  const [discountOnReorders, setDiscountOnReorders] = useState(true);
  const [optInToggle, setOptInToggle] = useState(false);
  const [itemFulfillmentImmediate, setitemFulfillmentImmediate] =
    useState(false);
  const [defaultFee, setDefaultFee] = useState('3');
  const [percentageInput, setPercentageInput] = useState({
    maximumFee: '1500',
    minimumFee: '1',
    percentage: '3',
  });
  const [currency, setCurrency] = useState('');
  const [showFixedModal, setShowFixedModal] = useState(false);
  const [showAllFixedRulesModal, setShowAllFixedRulesModal] = useState(false);
  const [showPercentageModal, setShowPercentageModal] = useState(false);
  const [rules, setRules] = useState<RuleItem[]>([]);
  const [currentProtectionData, setCurrentProtectionData] = useState<any>();
  const { storeId, storeProperties, refetchStoreProperties } = useStore();
  const isDisabled = storeProperties?.features?.['SUPPORT_SUBSCRIPTIONS']
    ? true
    : false;

  const [excludedProductSkus, setExcludedProductSkus] = useState<string[]>([]);
  const [excludedProductTags, setExcludedProductTags] = useState<string[]>([]);

  const [excludedCustomerTags, setExcludedCustomerTags] = useState<string[]>(
    [],
  );
  const confirmedExclusionsRef = useRef<Record<ExclusionField, string[]>>({
    excludedProductSkus: [],
    excludedProductTags: [],
    excludedCustomerTags: [],
  });
  const exclusionSaveTimersRef = useRef<
    Partial<Record<ExclusionField, ReturnType<typeof setTimeout>>>
  >({});
  const exclusionSaveRequestIdsRef = useRef<Record<ExclusionField, number>>({
    excludedProductSkus: 0,
    excludedProductTags: 0,
    excludedCustomerTags: 0,
  });

  const [getProtectionSettings, { loading: protectionLoading }] = useLazyQuery(
    GET_PROTECTION_SETTINGS,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        storeId,
      },
      onCompleted: (data) => {
        const protectionSettings = Array.isArray(data?.protectionSettings)
          ? data.protectionSettings[0]
          : undefined;

        setCurrentProtectionData(protectionSettings);
        if (
          Array.isArray(data?.protectionSettings) &&
          data?.protectionSettings?.[0]?.rules
        ) {
          setRules(data.protectionSettings[0].rules ?? []);
        } else {
          setRules([]);
        }

        if (
          protectionSettings?.defaultFee !== undefined &&
          protectionSettings?.defaultFee !== null
        ) {
          setDefaultFee(String(protectionSettings.defaultFee));
        }

        setPercentageInput((previousInput) => ({
          ...previousInput,
          maximumFee:
            protectionSettings?.maximumFee != null
              ? String(protectionSettings.maximumFee)
              : '',
          minimumFee:
            protectionSettings?.minimumFee != null
              ? String(protectionSettings.minimumFee)
              : '',
          percentage: `${getPreciseDecimal(
            Number(protectionSettings?.percentage ?? 0) * 100,
          )}`,
        }));
      },
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    },
  );

  const [setProtectionSettings, { loading }] = useMutation(
    UPDATE_PROTECTION_SETTINGS,
    {
      onCompleted: () => {
        getProtectionSettings();
        refetchStoreProperties && refetchStoreProperties();
      },
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    },
  );

  const [updateStore, { error: updateStoreError }] = useMutation(UPDATE_STORE, {
    onError(error) {
      const newError = apiErrorHandler(error);
      showNotification(NotificationTypes.error, newError?.message);
    },
  });

  const [updateCustomerExcludeTags] = useMutation(
    UPDATE_STORE_CUSTOMER_AUTO_OPT_IN_TAGS,
    {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    },
  );

  useEffect(() => {
    getProtectionSettings();
  }, [getProtectionSettings]);

  // Track when user visits the protection page for the first time
  useEffect(() => {
    if (
      storeProperties &&
      storeId &&
      !storeProperties.completedSteps?.hasVisitedProtectionPage
    ) {
      updateStore({
        variables: {
          storeId,
          set: {
            completedSteps: {
              ...storeProperties.completedSteps,
              hasVisitedProtectionPage: true,
            },
          },
        },
      });
    }
  }, [storeProperties, storeId, updateStore]);

  useEffect(() => {
    if (storeProperties) {
      setOptInToggle(storeProperties.widgetAutoOptIn);
      setDiscountOnReorders(storeProperties.reordersDiscount);
      setitemFulfillmentImmediate(storeProperties.itemFulfillmentImmediate);
      setCurrency(storeProperties.currency);

      const excludedProductSkus = Array.isArray(
        storeProperties.excludedProductSkus,
      )
        ? storeProperties.excludedProductSkus.filter(
            (item): item is string => typeof item === 'string' && !!item.trim(),
          )
        : [];
      const excludedProductTags = Array.isArray(
        storeProperties.excludedProductTags,
      )
        ? storeProperties.excludedProductTags.filter(
            (item): item is string => typeof item === 'string' && !!item.trim(),
          )
        : [];
      const tagsExcludedCustomersAutoOptIn = Array.isArray(
        storeProperties?.tagsExcludedCustomersAutoOptIn,
      )
        ? storeProperties.tagsExcludedCustomersAutoOptIn.filter(
            (item): item is string => typeof item === 'string' && !!item.trim(),
          )
        : [];

      setExcludedProductSkus(excludedProductSkus);
      setExcludedProductTags(excludedProductTags);
      setExcludedCustomerTags(tagsExcludedCustomersAutoOptIn);
      confirmedExclusionsRef.current = {
        excludedProductSkus,
        excludedProductTags,
        excludedCustomerTags: tagsExcludedCustomersAutoOptIn,
      };
    }
  }, [storeProperties]);

  useEffect(() => {
    const timers = exclusionSaveTimersRef.current;

    return () => {
      Object.values(timers).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  const handleSetSettings = (type: string) => {
    if (isDisabled) return;

    let input;
    if (type === 'fixed') {
      input =
        rules.length < 1
          ? {
              defaultFee: Number(defaultFee),
              type: 'FIXED',
              storeId,
            }
          : {
              defaultFee: Number(defaultFee),
              type: 'FIXED',
              storeId,
              rules: rules.map((rule, i) => ({
                ...rule,
                rangeLower: Number(rule.rangeLower),
                fee: Number(rule.fee),
                rangeUpper: Number(rule.rangeUpper),
                name: `Rule ${i + 1}`,
              })),
            };
    }
    if (type === 'percentage') {
      const minimumFee = getPriceValue(percentageInput.minimumFee);
      const maximumFee = getPriceValue(percentageInput.maximumFee);

      if (minimumFee > maximumFee) {
        return alert('Minimum fee should be lesser than maximum fee');
      }
      input = {
        ...percentageInput,
        storeId,
        type: 'PERCENTAGE',
        percentage: Number(percentageInput.percentage) / 100,
        minimumFee,
        maximumFee,
      };
    }
    setProtectionSettings({ variables: { input } }).then(() => {
      setShowFixedModal(false);
      setShowPercentageModal(false);
    });
  };

  const handleAddNewRule = () => {
    const newObj = {
      rangeLower: '0',
      fee: '0',
      rangeUpper: '0',
    };
    if (rules.length === 100) return;
    setRules([...rules, newObj]);
  };

  const handleRemoveRule = (ruleIndex: number) => {
    const filteredRules = [...rules].filter(
      (rule, index) => index !== ruleIndex,
    );
    setRules(filteredRules);
  };

  const handleChange = (field: string, value: string, index: number) => {
    const objIndex: number = [...rules].findIndex((obj, i) => i === index);
    const rulesCopy = cloneDeep([...rules]);

    if (field === 'rangeLower' && Number(value) >= 0)
      rulesCopy[objIndex].rangeLower = value;
    if (field === 'fee' && Number(value) >= 0) rulesCopy[objIndex].fee = value;
    if (field === 'rangeUpper' && Number(value) >= 0)
      rulesCopy[objIndex].rangeUpper = value;

    setRules(rulesCopy);
  };

  const handleChangePercentage = (field: string, value: string) => {
    if (Number(value) < 0) return;
    setPercentageInput({ ...percentageInput, [field]: value });
  };

  const handleUpdateStore = (payload: any) => {
    const { field, value, input, suppressSuccessNotification } = payload;
    return updateStore({
      variables: {
        storeId,
        set: input
          ? { ...input }
          : {
              [field]: value,
            },
      },
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    })
      .then((data) => {
        if (!suppressSuccessNotification) {
          return showNotification(
            NotificationTypes.success,
            'Store details updated',
          );
        }

        return data;
      })
      .catch(() => {
        return showNotification(
          NotificationTypes.error,
          updateStoreError ? updateStoreError.message : 'Details not updated',
        );
      });
  };

  const normalizeExclusionItems = (items: string[]) => {
    return Array.isArray(items)
      ? items
          .filter((item): item is string => typeof item === 'string')
          .map((item) => item.trim())
          .filter(Boolean)
      : [];
  };

  const rollbackExclusionField = (field: ExclusionField) => {
    const confirmedItems = confirmedExclusionsRef.current[field];

    if (field === 'excludedProductSkus') {
      setExcludedProductSkus(confirmedItems);
    } else if (field === 'excludedProductTags') {
      setExcludedProductTags(confirmedItems);
    } else {
      setExcludedCustomerTags(confirmedItems);
    }
  };

  const persistExclusionItems = (field: ExclusionField, items: string[]) => {
    if (!storeId) {
      showNotification(NotificationTypes.error, 'Store details not loaded');
      return;
    }

    const normalizedItems = normalizeExclusionItems(items);

    if (field === 'excludedProductSkus') {
      setExcludedProductSkus(normalizedItems);
    } else if (field === 'excludedProductTags') {
      setExcludedProductTags(normalizedItems);
    } else {
      setExcludedCustomerTags(normalizedItems);
    }

    const previousTimer = exclusionSaveTimersRef.current[field];
    if (previousTimer) clearTimeout(previousTimer);

    const requestId = exclusionSaveRequestIdsRef.current[field] + 1;
    exclusionSaveRequestIdsRef.current[field] = requestId;

    exclusionSaveTimersRef.current[field] = setTimeout(() => {
      const savePromise =
        field === 'excludedCustomerTags'
          ? updateCustomerExcludeTags({
              variables: {
                input: {
                  storeId,
                  tags: normalizedItems,
                },
              },
            })
          : updateStore({
              variables: {
                storeId,
                set: {
                  [field]: normalizedItems,
                },
              },
            });

      savePromise
        .then(() => {
          if (exclusionSaveRequestIdsRef.current[field] !== requestId) return;

          confirmedExclusionsRef.current[field] = normalizedItems;
        })
        .catch((error) => {
          if (exclusionSaveRequestIdsRef.current[field] !== requestId) return;

          rollbackExclusionField(field);
          const newError = apiErrorHandler(
            error as Parameters<typeof apiErrorHandler>[0],
          );
          showNotification(
            NotificationTypes.error,
            newError?.message || 'Details not updated',
          );
        })
        .finally(() => {
          if (exclusionSaveRequestIdsRef.current[field] === requestId) {
            delete exclusionSaveTimersRef.current[field];
          }
        });
    }, 250);
  };

  const allowAutoOptInSettings = useMemo(() => {
    return (
      isGodUser || storeProperties?.features?.['ALLOW_AUTO_OPTIN'] === true
    );
  }, [isGodUser, storeProperties]);
  const isInitialProtectionLoad =
    protectionLoading && currentProtectionData === undefined;
  const protectionType = currentProtectionData?.protectionType;
  const fixedActive = protectionType === 'FIXED';
  const percentageActive = protectionType === 'PERCENTAGE';
  const storeCurrency = currency || storeProperties?.currency || 'USD';
  const formattedDefaultFee =
    getStoreCurrencyFormatter(
      storeCurrency,
      currentProtectionData?.defaultFee ?? defaultFee,
    ) || getStoreCurrencyFormatter(storeCurrency, 0);
  const formattedMinimumFee =
    getStoreCurrencyFormatter(
      storeCurrency,
      percentageActive ? currentProtectionData?.minimumFee : 0,
    ) || getStoreCurrencyFormatter(storeCurrency, 0);
  const formattedMaximumFee =
    getStoreCurrencyFormatter(
      storeCurrency,
      percentageActive ? currentProtectionData?.maximumFee : 0,
    ) || getStoreCurrencyFormatter(storeCurrency, 0);
  const formattedPercentage = `${getPreciseDecimal(
    Number(
      percentageActive ? currentProtectionData?.percentage ?? 0.03 : 0.03,
    ) * 100,
  )}%`;

  return (
    <SettingsViewWrapper activeNav="protection">
      {isInitialProtectionLoad ? (
        <div className="min-h-[calc(100vh-220px)] flex items-center justify-center">
          <div
            style={{
              borderColor: 'rgba(37,99,235, 0.2)',
              borderTopColor: '#2564eb',
            }}
            className="inline-block border-4 rounded-full animate-spin w-24 h-24"
            role="status"
            aria-label="Loading Delivery Guarantee settings"
          />
        </div>
      ) : (
        <>
          <FixedModal
            show={showFixedModal}
            setShowModal={(val) => setShowFixedModal(val)}
            rules={rules}
            defaultFee={defaultFee}
            addNewRule={handleAddNewRule}
            removeRule={handleRemoveRule}
            onChangeRuleValue={handleChange}
            handleSetRule={handleSetSettings}
            isLoading={loading}
            setDefaultFee={(fee) => setDefaultFee(fee)}
            isDisabled={isDisabled}
          />
          <PercentageModal
            show={showPercentageModal}
            setShowModal={(val) => setShowPercentageModal(val)}
            isLoading={loading}
            handleSetRule={handleSetSettings}
            percentageInput={percentageInput}
            setInput={(field, value) => handleChangePercentage(field, value)}
            isDisabled={isDisabled}
          />
          <AllFixedRulesModal
            show={showAllFixedRulesModal}
            setShowModal={(val) => setShowAllFixedRulesModal(val)}
            rules={rules}
            isDisabled={isDisabled}
          />
          <main className="mx-auto max-w-7xl space-y-10 px-4 py-8 sm:px-6 md:px-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <h1 className="text-2xl font-semibold tracking-normal text-slate-950">
                  Delivery Guarantee Settings
                </h1>
                <p className="mt-2 max-w-3xl text-base leading-7 text-slate-500">
                  Configure how you offer shipping guarantees in your cart,
                  checkout, and product pages.
                </p>
              </div>
            </div>

            <section className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">
                  Delivery Guarantee Fee Type
                </h2>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  Select a fee type by choosing a card, then review and adjust
                  its values before saving.
                </p>
              </div>
              <div
                className={`grid gap-6 md:grid-cols-2 ${
                  isDisabled ? 'pointer-events-none opacity-50' : ''
                }`}
              >
                <div
                  role="button"
                  tabIndex={isDisabled ? -1 : 0}
                  className={`relative cursor-pointer overflow-hidden rounded-xl border bg-white p-6 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    fixedActive
                      ? 'border-2 border-blue-600'
                      : 'border-slate-200'
                  }`}
                  onClick={() => setShowFixedModal(true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setShowFixedModal(true);
                    }
                  }}
                >
                  {fixedActive && (
                    <div className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase text-blue-700">
                      <CheckCircleIcon className="h-4 w-4" />
                      Active
                    </div>
                  )}
                  <div className="mb-3 flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                      <CashIcon className="h-7 w-7" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-950">
                      Fixed Fee
                    </h3>
                  </div>
                  <p className="mt-1 min-h-[42px] text-sm leading-5 text-slate-500">
                    Charge a flat rate for every order regardless of the total
                    value.
                  </p>
                  <div className="mt-1.5 h-[86px] overflow-hidden rounded-lg bg-slate-50 p-2 text-xs">
                    {fixedActive && rules.length > 0 ? (
                      <div className="space-y-1">
                        {rules.slice(0, 2).map((rule, index) => (
                          <div
                            className="flex items-center justify-between gap-3"
                            key={`${rule.name || 'rule'}-${index}`}
                          >
                            <span className="font-semibold text-slate-700">
                              {getStoreCurrencyFormatter(
                                storeCurrency,
                                rule.fee,
                              )}
                            </span>
                            <span className="text-slate-500">
                              Orders{' '}
                              {getStoreCurrencyFormatter(
                                storeCurrency,
                                rule.rangeLower,
                              )}{' '}
                              -{' '}
                              {getStoreCurrencyFormatter(
                                storeCurrency,
                                rule.rangeUpper,
                              )}
                            </span>
                          </div>
                        ))}
                        {rules.length > 2 && (
                          <button
                            type="button"
                            className="pt-0.5 text-xs font-semibold text-blue-700 hover:text-blue-800"
                            onClick={(event) => {
                              event.stopPropagation();
                              setShowAllFixedRulesModal(true);
                            }}
                          >
                            See all rules
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="grid h-full grid-cols-2 items-center gap-3">
                        <div>
                          <p className="text-slate-500">Special rules</p>
                          <p className="font-semibold text-slate-900">
                            None configured
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">Default applies</p>
                          <p className="font-semibold text-slate-900">
                            All orders
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-4">
                    <span className="text-2xl font-bold text-slate-950">
                      {formattedDefaultFee}{' '}
                      <span className="text-sm font-normal text-slate-500">
                        / order
                      </span>
                    </span>
                    {fixedActive && (
                      <button
                        type="button"
                        className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700"
                        disabled={isDisabled}
                        onClick={(event) => {
                          event.stopPropagation();
                          setShowFixedModal(true);
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>

                <div
                  role="button"
                  tabIndex={isDisabled ? -1 : 0}
                  className={`relative cursor-pointer overflow-hidden rounded-xl border bg-white p-6 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    percentageActive
                      ? 'border-2 border-blue-600'
                      : 'border-slate-200'
                  }`}
                  onClick={() => setShowPercentageModal(true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setShowPercentageModal(true);
                    }
                  }}
                >
                  {percentageActive && (
                    <div className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase text-blue-700">
                      <CheckCircleIcon className="h-4 w-4" />
                      Active
                    </div>
                  )}
                  <div className="mb-3 flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                      <CurrencyDollarIcon className="h-7 w-7" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-950">
                      Percentage Fee
                    </h3>
                  </div>
                  <p className="mt-1 min-h-[42px] text-sm leading-5 text-slate-500">
                    Calculate the fee based on a percentage of the total order
                    value.
                  </p>
                  <div className="mt-1.5 grid h-[86px] grid-cols-2 items-center gap-3 rounded-lg bg-slate-50 p-2 text-xs">
                    <div>
                      <p className="text-slate-500">Minimum</p>
                      <p className="font-semibold text-slate-900">
                        {formattedMinimumFee}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Maximum</p>
                      <p className="font-semibold text-slate-900">
                        {formattedMaximumFee}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-4">
                    <span className="text-2xl font-bold text-slate-950">
                      {formattedPercentage}{' '}
                      <span className="text-sm font-normal text-slate-500">
                        / order
                      </span>
                    </span>
                    {percentageActive && (
                      <button
                        type="button"
                        className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700"
                        disabled={isDisabled}
                        onClick={(event) => {
                          event.stopPropagation();
                          setShowPercentageModal(true);
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <OfferingSettings />

            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-950">
                Cart Settings
              </h2>
              <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="space-y-6">
                  {allowAutoOptInSettings && (
                    <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-6">
                      <div>
                        <h3 className="text-base font-semibold text-slate-950">
                          Auto Opt-in
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                          Automatically select Delivery Guarantee when the
                          widget loads.
                        </p>
                      </div>
                      <Toggle
                        isEnabled={optInToggle}
                        onChange={(value) => {
                          setOptInToggle(value);
                          handleUpdateStore({
                            field: 'widgetAutoOptIn',
                            value,
                          });
                        }}
                      />
                    </div>
                  )}
                  <div className="grid gap-3 md:grid-cols-[minmax(180px,0.35fr)_minmax(0,1fr)] md:items-start">
                    <div>
                      <h3 className="text-base font-semibold text-slate-950">
                        Fulfill when
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Choose when guarantee items should be fulfilled.
                      </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {[
                        {
                          label: 'Other items fulfilled',
                          description:
                            'Mark fulfilled when the rest of the order is fulfilled.',
                          value: false,
                        },
                        {
                          label: 'Purchased immediately',
                          description:
                            'Fulfill as soon as the order is placed.',
                          value: true,
                        },
                      ].map((option) => {
                        const isSelected =
                          itemFulfillmentImmediate === option.value;

                        return (
                          <button
                            key={option.label}
                            type="button"
                            className={`flex h-full items-start gap-2 rounded-lg border p-3 text-left transition ${
                              isSelected
                                ? 'border-blue-600 bg-blue-50 shadow-sm'
                                : 'border-slate-200 bg-white hover:border-blue-200 hover:bg-slate-50'
                            }`}
                            aria-pressed={isSelected}
                            onClick={() => {
                              setitemFulfillmentImmediate(option.value);
                              handleUpdateStore({
                                field: 'itemFulfillmentImmediate',
                                value: option.value,
                              });
                            }}
                          >
                            <span
                              className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border ${
                                isSelected
                                  ? 'border-blue-600 bg-blue-600'
                                  : 'border-slate-300 bg-white'
                              }`}
                            >
                              {isSelected && (
                                <span className="h-2 w-2 rounded-full bg-white" />
                              )}
                            </span>
                            <span>
                              <span className="block whitespace-nowrap text-sm font-semibold text-slate-950">
                                {option.label}
                              </span>
                              <span className="mt-1 block text-sm leading-5 text-slate-500">
                                {option.description}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-950">
                Resolutions
              </h2>
              <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-700">
                    <RefreshIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-950">
                      100% Discount on Reorders
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                      When resolving an issue with a replacement, apply a 100%
                      discount automatically.
                    </p>
                  </div>
                </div>
                <Toggle
                  isEnabled={discountOnReorders}
                  onChange={(value) => {
                    setDiscountOnReorders(value);
                    handleUpdateStore({ field: 'reordersDiscount', value });
                  }}
                />
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-950">
                Advanced Exclusion Settings
              </h2>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="space-y-6 p-6">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                    <TagIcon className="h-5 w-5 text-blue-700" />
                    <h3 className="text-lg font-semibold text-slate-950">
                      Product Exclusions
                    </h3>
                  </div>

                  <div className="space-y-4">
                    <TagInputCard
                      title="Exclude by SKUs"
                      description="Prevent Delivery Guarantee from appearing on specific product identifiers."
                      placeholder="Enter SKU"
                      items={excludedProductSkus}
                      onChangeItems={(items) =>
                        persistExclusionItems('excludedProductSkus', items)
                      }
                    />
                    <TagInputCard
                      title="Exclude by Product Tags"
                      description="Exclude groups of products based on their Shopify tags."
                      placeholder="Enter product tag"
                      items={excludedProductTags}
                      onChangeItems={(items) =>
                        persistExclusionItems('excludedProductTags', items)
                      }
                    />
                    <TagInputCard
                      title="Exclude by Customer Tags"
                      description="Hide Delivery Guarantee auto opt-in for specific customer segments."
                      placeholder="Enter customer tag"
                      items={excludedCustomerTags}
                      onChangeItems={(items) =>
                        persistExclusionItems('excludedCustomerTags', items)
                      }
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-4 pb-10">
              <h2 className="text-lg font-semibold text-slate-950">
                Global Settings
              </h2>
              <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="grid gap-6 md:grid-cols-2 md:items-start">
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-slate-950">
                      Store Currency
                    </h3>
                    <p className="text-sm leading-6 text-slate-500">
                      Pulled from your Shopify store currency and used to
                      calculate prices across all guarantee widgets.
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center md:pt-8">
                    <div className="flex min-w-[160px] items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
                      <span className="text-sm font-medium text-slate-500">
                        Currency
                      </span>
                      <span className="text-lg font-bold text-slate-950">
                        {storeCurrency}
                      </span>
                    </div>
                    <div className="flex flex-1 items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs font-semibold text-blue-700">
                      <ShieldCheckIcon className="h-4 w-4 flex-shrink-0" />
                      <span>
                        This value comes from your Shopify store&apos;s base
                        currency.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </main>
        </>
      )}
    </SettingsViewWrapper>
  );
});
